import { Tabs, TabsList, TabsTrigger } from '@sr/ui-library';
import type { ReactNode } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { workspaceTypeIsAgent } from '../utils/helpers/common';
import { MSR3BaseLayout } from './MSR3BaseLayout';

export type RoyaltiesSectionTab = {
  /** When true, tab is only shown for agent workspaces. */
  agentOnly: boolean;
  label: string;
  section: string;
};

export type RoyaltiesSectionTabsLayoutProps = {
  /** Optional content above the tab outlet (e.g. historical cut-off banner). */
  aboveOutlet?: ReactNode;
  /** URL segment under `/workspace/:id/` (e.g. `royalties`, `royalties-anz`). */
  basePathSegment: string;
  defaultSection: string;
  /** Optional classes on the outlet wrapper (e.g. ANZ banner spacing). */
  outletWrapperClassName?: string;
  pageTitle: string;
  tabs: readonly RoyaltiesSectionTab[];
};

/**
 * Shared MSR3 shell for Historic Royalties and Royalties ANZ: page header,
 * agent-aware section tabs, and an outlet for tab content.
 */
export const RoyaltiesSectionTabsLayout = ({
  aboveOutlet,
  basePathSegment,
  defaultSection,
  outletWrapperClassName,
  pageTitle,
  tabs,
}: RoyaltiesSectionTabsLayoutProps) => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [activeWorkspace] = useActiveWorkspace();
  const location = useLocation();
  const navigate = useNavigate();

  const isAgent = workspaceTypeIsAgent(activeWorkspace);
  const sectionMatch = location.pathname.match(
    new RegExp(`/${basePathSegment}/([^/]+)`),
  )?.[1];
  const section =
    sectionMatch && tabs.some((t) => t.section === sectionMatch)
      ? sectionMatch
      : defaultSection;

  const tabLink = (segment: string) =>
    workspaceId
      ? `/workspace/${workspaceId}/${basePathSegment}/${segment}`
      : undefined;

  const visibleTabs = tabs.filter(({ agentOnly }) => !agentOnly || isAgent);
  const showTabBar = visibleTabs.length > 1;

  return (
    <MSR3BaseLayout
      pageHeader={
        <PageHeader title={pageTitle}>
          {showTabBar && (
            <Tabs
              className="w-fit"
              value={section}
              onValueChange={(value) => {
                const to = tabLink(value);

                if (to) navigate(to);
              }}
            >
              <TabsList>
                {visibleTabs.map(({ label, section: tabSection }) => (
                  <TabsTrigger key={tabSection} value={tabSection}>
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}
        </PageHeader>
      }
    >
      <div className={outletWrapperClassName}>
        {aboveOutlet}
        <Outlet />
      </div>
    </MSR3BaseLayout>
  );
};
